'use client'

import * as Sentry from '@sentry/nextjs'
import Link from 'next/link'
import { useMemo } from 'react'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { ISearchResult } from '@/_types/search'
import { sendSearchResultClickEvent } from '@/_utils/analytics.utils'

import {
	getMatchedFieldDescription,
	getMatchedFieldName,
	getModelName,
	getSearchResultUrl
} from './_utils/search-result.utils'

interface AMobileSearchResultsProps {
	results: ISearchResult[]
	isLoading: boolean
	isSearching: boolean
	hasSearchTerm: boolean
	onResultClick: () => void
	searchQuery?: string
}

interface ProcessedResult {
	key: string
	url: string
	title: string
	description: string | null
}

export const AMobileSearchResults = ({
	results,
	isLoading,
	isSearching,
	hasSearchTerm,
	onResultClick,
	searchQuery = ''
}: AMobileSearchResultsProps) => {
	try {
		const processedResults = useMemo<ProcessedResult[]>(() => {
			return results.map(result => {
				const title = getMatchedFieldName(result)
				const description = getMatchedFieldDescription(result) || getModelName(result)

				return {
					key: `${result.model}-${result.id}`,
					url: getSearchResultUrl(result),
					title,
					description
				}
			})
		}, [results])

		if (!hasSearchTerm) {
			return null
		}

		if (isSearching || isLoading) {
			return (
				<div className='header-mobile__search-results'>
					<div className='header-mobile__search-results-loading'>Vyhľadávanie...</div>
				</div>
			)
		}

		if (results.length === 0) {
			return (
				<div className='header-mobile__search-results'>
					<div className='header-mobile__search-results-empty'>
						Nenašli sa žiadne výsledky
					</div>
				</div>
			)
		}

		return (
			<div className='header-mobile__search-results'>
				<div className='header-mobile__search-results-list'>
					{processedResults.map(result => (
						<Link
							key={result.key}
							href={nextLinkHrefFromPathWithOptionalHash(result.url)}
							className='header-mobile__search-result-item'
							onClick={() => {
								// Send GA event for search result click with destination URL
								sendSearchResultClickEvent(searchQuery, result.url)
								onResultClick()
							}}
						>
							<div
								className='header-mobile__search-result-title'
								dangerouslySetInnerHTML={{ __html: result.title }}
							/>
							{result.description && (
								<div
									className='header-mobile__search-result-description'
									dangerouslySetInnerHTML={{ __html: result.description }}
								/>
							)}
						</Link>
					))}
				</div>
			</div>
		)
	} catch (error) {
		Sentry.captureException(error)
		return null
	}
}
